/**
 * Cryptography utilities for end-to-end encryption
 * Uses Web Crypto API with AES-GCM and PBKDF2
 */

const ITERATIONS = 100000 // PBKDF2 iterations
const KEY_LENGTH = 256 // AES key length in bits
const IV_LENGTH = 12 // GCM IV length in bytes
const SALT_LENGTH = 16 // Salt length in bytes

/**
 * Derives an encryption key from a password using PBKDF2
 * @param {string} password - The password to derive from
 * @param {Uint8Array} salt - Random salt
 * @returns {Promise<CryptoKey>} The derived key
 */
export async function deriveKey(password, salt) {
  const encoder = new TextEncoder()
  const passwordBuffer = encoder.encode(password)
  
  // Import the password as a key
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  )
  
  // Derive the actual encryption key
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: ITERATIONS,
      hash: 'SHA-256'
    },
    passwordKey,
    {
      name: 'AES-GCM',
      length: KEY_LENGTH
    },
    true,
    ['encrypt', 'decrypt']
  )
}

/**
 * Encrypts data using AES-GCM
 * @param {ArrayBuffer} data - The data to encrypt
 * @param {string} password - The password for encryption
 * @returns {Promise<Blob>} Encrypted blob containing salt + IV + encrypted data
 */
export async function encryptData(data, password) {
  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH))
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH))
  
  // Derive encryption key
  const key = await deriveKey(password, salt)
  
  // Encrypt the data
  const encryptedData = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv
    },
    key,
    data
  )
  
  // Combine salt + IV + encrypted data
  const result = new Uint8Array(salt.length + iv.length + encryptedData.byteLength)
  result.set(salt, 0)
  result.set(iv, salt.length)
  result.set(new Uint8Array(encryptedData), salt.length + iv.length)
  
  return new Blob([result])
}

/**
 * Decrypts data using AES-GCM
 * @param {ArrayBuffer} encryptedData - The encrypted data (salt + IV + encrypted)
 * @param {string} password - The password for decryption
 * @returns {Promise<ArrayBuffer>} The decrypted data
 */
export async function decryptData(encryptedData, password) {
  const data = new Uint8Array(encryptedData)
  
  // Extract salt, IV, and encrypted data
  const salt = data.slice(0, SALT_LENGTH)
  const iv = data.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH)
  const encrypted = data.slice(SALT_LENGTH + IV_LENGTH)
  
  // Derive decryption key
  const key = await deriveKey(password, salt)
  
  try {
    // Decrypt the data
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encrypted
    )
    
    return decryptedData
  } catch (error) {
    throw new Error('Invalid password or corrupted file')
  }
}

/**
 * Generates a secure random password
 * @param {number} length - Password length
 * @returns {string} Random password
 */
export function generateSecurePassword(length = 16) {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  
  return Array.from(array, byte => charset[byte % charset.length]).join('')
}

/**
 * Validates password strength (simplified for 4+ characters)
 * @param {string} password - Password to validate
 * @returns {object} Validation result with score and feedback
 */
export function validatePasswordStrength(password) {
  const result = {
    score: 0,
    feedback: [],
    isStrong: false
  }
  
  // Simple validation: just need 4+ characters (letters or numbers)
  if (password.length >= 4) {
    result.score = 5 // Max score for simplicity
    result.isStrong = true
  } else {
    result.feedback.push('Use at least 4 characters (letters or numbers)')
  }
  
  return result
}
